import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

const GuarantorReview = () => {
  const { token } = useParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countries, setCountries] = useState([]);
  const [leaseData, setLeaseData] = useState(null);
  const [agreed, setAgreed] = useState(false);

  // Step 1 form
  const [ge, setGe] = useState('');
  const [gp, setGp] = useState('');
  const [gcc, setGcc] = useState('+1');
  const [tn, setTn] = useState('');
  const [tp, setTp] = useState('');
  const [tcc, setTcc] = useState('+1');
  const [rel, setRel] = useState('parent');
  const [verifyData, setVerifyData] = useState(null);
  const [gcode, setGcode] = useState('');

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/business-main/countries`);
        const data = await res.json();
        if (data.countries?.length > 0) {
          setCountries(data.countries);
          if (data.countries.length === 1) {
            setGcc(data.countries[0].phone_code);
            setTcc(data.countries[0].phone_code);
          }
        }
      } catch {
        setCountries([{ country_id: 'us', name: 'United States', phone_code: '+1' }]);
      }
    };
    fetchCountries();
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!ge.trim() && !gp.trim()) {
      setError('Please provide your email or phone number.');
      return;
    }
    if (!tn.trim()) {
      setError('Please provide the tenant\'s name.');
      return;
    }
    if (!rel) {
      setError('Please select your relationship.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'gv',
          gt: token,
          ge: ge.trim() || undefined,
          gp: gp.trim() || undefined,
          gcc: gp.trim() ? gcc : undefined,
          tn: tn.trim(),
          tp: tp.trim() || undefined,
          rel: rel,
        }),
      });
      const result = await res.json();
      if (result.success) {
        if (result.data?.step === 'verify_code') {
          setVerifyData(result.data);
          setStep(1.5);
        } else {
          setLeaseData(result.data);
          setStep(2);
        }
      } else {
        setError(result.message || 'Verification failed. Please check your details.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!gcode.trim()) {
      setError('Please enter the verification code.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'gvc',
          gt: token,
          gcode: gcode.trim(),
          ge: ge.trim() || undefined,
          gp: gp.trim() || undefined,
        }),
      });
      const result = await res.json();
      if (result.success) {
        setLeaseData(result.data);
        setStep(2);
      } else {
        setError(result.message || 'Invalid verification code.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'ga',
          gt: token,
          ge: ge.trim() || undefined,
          gp: gp.trim() || undefined,
        }),
      });
      const result = await res.json();
      if (result.success) {
        setLeaseData(prev => ({ ...prev, aa: result.data?.aa, st: result.data?.st }));
        setStep(3);
      } else {
        setError(result.message || 'Failed to record acknowledgment.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      const [y, m, day] = d.split('T')[0].split('-');
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${months[parseInt(m, 10) - 1]} ${parseInt(day, 10)}, ${y}`;
    } catch { return d; }
  };

  const formatCurrency = (amount, cur) => {
    if (!amount && amount !== 0) return '—';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur || 'USD' }).format(amount);
    } catch { return `$${amount}`; }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center px-4 py-8 sm:py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-6">
          {/* <h1 className="text-2xl font-bold text-gray-900 font-sans">EzDaily<span className="text-emerald-600">AI</span></h1> */}
          <h1 className="text-2xl font-bold text-gray-900 font-sans">EzDaily</h1>
          <p className="text-sm text-gray-500 mt-1">Guarantor review</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6 px-4">
          {[1, 1.5, 2, 3].map((s, i, arr) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                step >= s ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'
              }`}>{i + 1}</div>
              {i < arr.length - 1 && <div className={`flex-1 h-0.5 transition-colors ${step > s ? 'bg-emerald-300' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* ═══ STEP 1 ═══ */}
        {step === 1 && (
          <form onSubmit={handleVerify} className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                <svg className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Verify your identity</h2>
            </div>
            <p className="text-sm text-gray-500 mb-5">Confirm your details and the tenant's information to proceed.</p>

            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Your details</p>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Email</label>
              <input type="email" value={ge} onChange={e => setGe(e.target.value)} placeholder="your@email.com"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400" />
            </div>
            <div className="grid grid-cols-[85px_1fr] gap-2 mb-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Code</label>
                <select value={gcc} onChange={e => setGcc(e.target.value)}
                  className="w-full px-2 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 bg-white">
                  {countries.map(c => <option key={c.country_id} value={c.phone_code}>{c.phone_code}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Phone</label>
                <input type="tel" value={gp} onChange={e => setGp(e.target.value)} placeholder="555-123-4567"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400" />
              </div>
            </div>

            <div className="border-t border-gray-100 my-4 pt-3">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Tenant details</p>
            </div>
            <div className="mb-3">
              <label className="block text-sm text-gray-600 mb-1">Tenant's name <span className="text-red-400">*</span></label>
              <input type="text" value={tn} onChange={e => setTn(e.target.value)} placeholder="John Smith" required
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400" />
            </div>
            <div className="grid grid-cols-[85px_1fr] gap-2 mb-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Code</label>
                <select value={tcc} onChange={e => setTcc(e.target.value)}
                  className="w-full px-2 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 bg-white">
                  {countries.map(c => <option key={c.country_id} value={c.phone_code}>{c.phone_code}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Tenant's phone</label>
                <input type="tel" value={tp} onChange={e => setTp(e.target.value)} placeholder="555-987-6543"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400" />
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-sm text-gray-600 mb-1">Relationship <span className="text-red-400">*</span></label>
              <select value={rel} onChange={e => setRel(e.target.value)} required
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 bg-white">
                <option value="parent">Parent</option>
                <option value="relative">Relative</option>
                <option value="employer">Employer</option>
                <option value="other">Other</option>
              </select>
            </div>

            {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mb-4">{error}</p>}

            <button type="submit" disabled={loading}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg text-sm transition-colors disabled:opacity-50">
              {loading ? 'Verifying...' : 'Verify my identity'}
            </button>
          </form>
        )}

        {/* ═══ STEP 1.5 — Verify Code ═══ */}
        {step === 1.5 && verifyData && (
          <form onSubmit={handleVerifyCode} className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Enter verification code</h2>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              We sent a 6-digit code to <span className="font-medium text-gray-700">{verifyData.masked}</span>. Please enter it below.
            </p>

            <div className="mb-5">
              <label className="block text-sm text-gray-600 mb-1">Verification code <span className="text-red-400">*</span></label>
              <input type="text" value={gcode} onChange={e => setGcode(e.target.value)} placeholder="000000"
                maxLength={6} inputMode="numeric" autoFocus
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-center tracking-widest text-lg focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400" />
            </div>

            <p className="text-xs text-gray-400 mb-4">Code expires in 10 minutes.</p>

            {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mb-4">{error}</p>}

            <button type="submit" disabled={loading}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg text-sm transition-colors disabled:opacity-50">
              {loading ? 'Verifying...' : 'Verify code'}
            </button>

            <button type="button" onClick={() => { setStep(1); setError(''); setGcode(''); }}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 mt-2 transition-colors">
              Back to details
            </button>
          </form>
        )}

        {/* ═══ STEP 2 ═══ */}
        {step === 2 && leaseData && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Review lease details</h2>
            </div>
            <p className="text-sm text-gray-500 mb-5">Review the lease you're guaranteeing for <span className="font-medium text-gray-700">{leaseData.tn}</span>.</p>

            {/* Lease info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-3">
              <table className="w-full text-sm">
                <tbody>
                  {[
                    ['Tenant', leaseData.tn, true],
                    ['Property', leaseData.pt, true],
                    ['Address', leaseData.pa, false],
                    ['Landlord', leaseData.ln, false],
                    ['Lease period', `${formatDate(leaseData.sd)} – ${formatDate(leaseData.ed)}`, false],
                    ['Duration', leaseData.dur, false],
                  ].map(([label, value, bold], i) => (
                    <tr key={i}>
                      <td className="text-gray-500 py-1.5 pr-3 align-top">{label}</td>
                      <td className={`text-right py-1.5 ${bold ? 'font-medium text-gray-900' : 'text-gray-700'}`}>{value || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Monthly rent */}
            <div className="bg-gray-50 rounded-lg p-4 mb-3">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Monthly charges</p>
              <table className="w-full text-sm">
                <tbody>
                  {leaseData.smit?.length > 0 ? (
                    <>
                      {leaseData.smit.map((item, i) => (
                        <tr key={i}>
                          <td className="text-gray-500 py-1.5 pr-3">{item.name}</td>
                          <td className="text-right py-1.5 text-gray-700">{formatCurrency(item.amount, leaseData.cur)}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-gray-200">
                        <td className="text-gray-900 font-medium py-2 pr-3">Tenant pays monthly</td>
                        <td className="text-right py-2 font-medium text-gray-900">{formatCurrency(leaseData.stm, leaseData.cur)}</td>
                      </tr>
                    </>
                  ) : (
                    <>
                      <tr>
                        <td className="text-gray-500 py-1.5 pr-3">Total monthly rent</td>
                        <td className="text-right py-1.5 text-gray-700">{formatCurrency(leaseData.tma ?? leaseData.ra, leaseData.cur)}</td>
                      </tr>
                      {leaseData.rsp != null && (
                        <tr>
                          <td className="text-gray-500 py-1.5 pr-3">Tenant's share</td>
                          <td className="text-right py-1.5 text-gray-700">{leaseData.rsp}%</td>
                        </tr>
                      )}
                      <tr className="border-t border-gray-200">
                        <td className="text-gray-900 font-medium py-2 pr-3">Tenant pays monthly</td>
                        <td className="text-right py-2 font-medium text-gray-900">{formatCurrency(leaseData.stm ?? leaseData.sra ?? leaseData.ra, leaseData.cur)}</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>

            {/* Move-in costs */}
            {(leaseData.smi?.length > 0 || leaseData.sma != null) && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Move-in costs</p>
                <table className="w-full text-sm">
                  <tbody>
                    {(leaseData.smi || []).map((item, i) => (
                      <tr key={i}>
                        <td className="text-gray-500 py-1.5 pr-3">{item.name}</td>
                        <td className="text-right py-1.5 text-gray-700">{formatCurrency(item.amount, leaseData.cur)}</td>
                      </tr>
                    ))}
                    {leaseData.sma != null && (
                      <tr className="border-t border-gray-200">
                        <td className="text-gray-900 font-medium py-2 pr-3">Tenant's move-in total</td>
                        <td className="text-right py-2 font-medium text-gray-900">{formatCurrency(leaseData.sma, leaseData.cur)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {leaseData.aa ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium text-emerald-800">Acknowledged</span>
                  </div>
                  <span className="text-xs text-emerald-600">
                    {(() => {
                      try {
                        const d = new Date(leaseData.aa);
                        const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                        const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                        const tz = d.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                        return `${dateStr} ${timeStr} ${tz}`;
                      } catch { return ''; }
                    })()}
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5">
                  <p className="text-sm font-medium text-amber-800 mb-2">Your responsibilities as guarantor</p>
                  <ul className="text-xs text-amber-700 space-y-1.5 list-disc pl-4">
                    <li>Cover unpaid rent if the tenant defaults on payment</li>
                    <li>Cover damages beyond the security deposit amount</li>
                    <li>This obligation lasts for the full lease term</li>
                    <li>The landlord may contact you if rent is overdue</li>
                  </ul>
                </div>

                <label className="flex items-start gap-2.5 mb-5 cursor-pointer">
                  <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-emerald-600 rounded" />
                  <span className="text-sm text-gray-700 leading-relaxed">
                    I, <span className="font-medium">{leaseData.gn}</span>, acknowledge and agree to act as guarantor for this lease and accept the responsibilities listed above.
                  </span>
                </label>

                {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mb-4">{error}</p>}

                <button onClick={handleAcknowledge} disabled={!agreed || loading}
                  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? 'Confirming...' : 'Confirm acknowledgment'}
                </button>
              </>
            )}

            <button onClick={() => { setStep(1); setError(''); }}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 mt-2 transition-colors">
              Back to verification
            </button>
          </div>
        )}

        {/* ═══ STEP 3 ═══ */}
        {step === 3 && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Thank you{leaseData?.gn ? `, ${leaseData.gn.split(' ')[0]}` : ''}!</h2>
            <p className="text-sm text-gray-500 mb-4">Your acknowledgment has been recorded. The tenant can now proceed with accepting their lease.</p>
            <div className="bg-gray-50 rounded-lg px-4 py-3 text-xs text-gray-400">
              Recorded on {formatDate(leaseData?.aa || new Date().toISOString())}
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">Powered by EzDaily Inc.</p>
      </div>
    </div>
  );
};

export default GuarantorReview;
